import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  FaShoppingCart,
  FaUser,
  FaMapMarkerAlt,
  FaPhone,
  FaCalendarAlt,
  FaEye,
  FaCheckCircle,
  FaTrash,
  FaFilter,
  FaSearch,
  FaFileExcel,
  FaBell,
  FaTag
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import ExcelJS from 'exceljs';
import { sendStatusUpdateNotification } from '../../utils/emailService';
import firebaseService from '../../services/firebaseService';
import PrintOrder from './PrintOrder';
import notificationService from '../../services/notificationService';
import emailService from '../../services/emailService';
import { useCustomAlert } from '../../hooks/useCustomAlert';
import CustomAlert from '../CustomAlert/CustomAlert';
import { useLoadingContext } from '../Loading/LoadingProvider';
import Loading from '../Loading/Loading';
import Notification from '../Notification/Notification';
import PasswordDialog from '../PasswordDialog/PasswordDialog';
import NotificationManager from '../Notifications/NotificationManager';
import './AdminOrders.css';

function AdminOrders() {
  const navigate = useNavigate();
  const { alertState, showAlert } = useCustomAlert();
  const { startComponentLoading, stopComponentLoading, isComponentLoading } = useLoadingContext();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [newOrders, setNewOrders] = useState(new Set());
  const [showNotification, setShowNotification] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showNotificationManager, setShowNotificationManager] = useState(false);
  const [showPrintOrder, setShowPrintOrder] = useState(false);
  const [printOrderData, setPrintOrderData] = useState(null);
  const [showDeletedOrders, setShowDeletedOrders] = useState(false);
  const [deletedOrdersCount, setDeletedOrdersCount] = useState(0);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);

  // Helper function to get full customer name
  const getFullCustomerName = (order) => {
    const customer = order.customer || order.customerData || {};
    const firstName = customer.fullName || '';
    const lastName = customer.lastName || '';

    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else {
      return 'N/A';
    }
  };

  // Check if admin is logged in and initialize notifications
  useEffect(() => {
    const isLoggedIn = localStorage.getItem('adminLoggedIn');
    if (!isLoggedIn) {
      navigate('/admin/login');
      return;
    }

    // Initialize notification service
    const initNotifications = async () => {
      try {
        console.log('🔔 Initializing notification service...');
        await notificationService.initialize();
        console.log('✅ Notification service initialized successfully');
      } catch (error) {
        console.error('❌ Failed to initialize notification service:', error);
      }
    };

    initNotifications();

    // Subscribe to maintenance mode changes
    const unsubMaintenance = firebaseService.subscribeToMaintenanceMode((result) => {
      setMaintenanceMode(result.isEnabled);
    });

    return () => {
      unsubMaintenance && unsubMaintenance();
    };
  }, [navigate]);

  // Load show/hide preference and sync across devices
  useEffect(() => {
    const savedPreference = localStorage.getItem('showDeletedOrders');
    const initialShow = savedPreference === 'true';
    setShowDeletedOrders(initialShow);

    // Listen for storage changes from other devices
    const handleStorageChange = (e) => {
      if (e.key === 'showDeletedOrders') {
        const newShowDeleted = e.newValue === 'true';
        setShowDeletedOrders(newShowDeleted);
        console.log('🔄 Show deleted orders preference synced from other device:', newShowDeleted);
      }
      if (e.key === 'deletedOrders') {
        const deletedOrders = JSON.parse(e.newValue || '[]');
        setDeletedOrdersCount(deletedOrders.length);
        console.log('🔄 Deleted orders synced from other device:', deletedOrders.length);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Consolidated Real-Time Sync
  useEffect(() => {
    startComponentLoading('mainOrders', 'Connecting...');

    // Subscribe to active orders
    const unsubscribe = firebaseService.subscribeToOrders((result) => {
      if (result.success) {
        setOrders(result.orders);
        setFilteredOrders(result.orders);
        setNotificationCount(result.orders.filter(o => o.status === 'new').length);
      }
      stopComponentLoading('mainOrders');
    });

    return () => unsubscribe && unsubscribe();
  }, [startComponentLoading, stopComponentLoading]);



  // Hide notification when admin panel is opened
  useEffect(() => {
    if (window.location.pathname.includes('/admin/orders')) {
      setShowNotification(false);
    }
  }, []);


  // Periodic sync for deleted orders count
  useEffect(() => {
    const syncDeletedData = async () => {
      const res = await firebaseService.getDeletedOrders();
      if (res.success) {
        setDeletedOrdersCount(res.deletedOrders.length);
        localStorage.setItem('deletedOrders', JSON.stringify(res.deletedOrders));
      }
    };

    syncDeletedData();
    const intervalId = setInterval(syncDeletedData, 30000); // Sync bin count every 30s
    return () => clearInterval(intervalId);
  }, []);
  // Sorting and filtering logic remains clean below.


  // Sort orders function
  const sortOrders = useCallback((ordersToSort, sortType) => {
    const sorted = [...ordersToSort];

    switch (sortType) {
      case 'newest':
        return sorted.sort((a, b) => new Date(b.date) - new Date(a.date));
      case 'oldest':
        return sorted.sort((a, b) => new Date(a.date) - new Date(b.date));
      case 'highest':
        return sorted.sort((a, b) => (b.total || b.finalTotal || 0) - (a.total || a.finalTotal || 0));
      case 'lowest':
        return sorted.sort((a, b) => (a.total || a.finalTotal || 0) - (b.total || b.finalTotal || 0));
      case 'name-asc':
        return sorted.sort((a, b) => {
          const nameA = getFullCustomerName(a).toLowerCase();
          const nameB = getFullCustomerName(b).toLowerCase();
          return nameA.localeCompare(nameB);
        });
      case 'name-desc':
        return sorted.sort((a, b) => {
          const nameA = getFullCustomerName(a).toLowerCase();
          const nameB = getFullCustomerName(b).toLowerCase();
          return nameB.localeCompare(nameA);
        });
      default:
        return sorted;
    }
  }, []);

  // Filter and sort orders
  useEffect(() => {
    let filtered = orders;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(order =>
        getFullCustomerName(order).toLowerCase().includes(searchTerm.toLowerCase()) ||
        (order.customer?.phone1 || order.customerData?.phone1 || '').includes(searchTerm) ||
        (order.customer?.governorate || order.customerData?.governorate || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    // Sort orders
    const sorted = sortOrders(filtered, sortBy);
    setFilteredOrders(sorted);
  }, [orders, searchTerm, statusFilter, sortBy, sortOrders]);

  const updateOrderStatus = async (orderId, newStatus) => {
    const order = orders.find(order => order.id === orderId);
    const oldStatus = order?.status;

    // Update in Firebase
    const firebaseResult = await firebaseService.updateOrderStatus(orderId, newStatus);

    if (firebaseResult.success) {
      console.log(`✅ Order ${orderId} status updated to ${newStatus} in Firebase`);

      // Also update localStorage as backup
      const updatedOrders = orders.map(order =>
        order.id === orderId ? { ...order, status: newStatus } : order
      );
      localStorage.setItem('orders', JSON.stringify(updatedOrders));

      // Send status update notification
      if (order && oldStatus !== newStatus) {
        // Send email notification
        try {
          const emailResult = await emailService.sendOrderStatusUpdateEmail(order, newStatus);
          if (emailResult.success) {
            console.log('✅ Status update email sent successfully');
          } else {
            console.error('❌ Failed to send status update email:', emailResult.error);
          }
        } catch (error) {
          console.error('❌ Email sending error:', error);
        }

        // Also send the old notification system
        sendStatusUpdateNotification(order, oldStatus, newStatus).then(result => {
          if (result.success) {
            console.log('Status update notification sent successfully');
          } else {
            console.error('Failed to send status update notification:', result.error);
          }
        });
      }
    } else {
      console.error('❌ Failed to update order status in Firebase:', firebaseResult.error);
      alert('حدث خطأ في تحديث حالة الطلب. يرجى المحاولة مرة أخرى.');
    }

    // Send notification for status update
    if (firebaseResult.success && order && oldStatus !== newStatus) {
      try {
        await notificationService.sendOrderStatusNotification(order, oldStatus, newStatus);
        console.log('✅ Status update notification sent');
      } catch (error) {
        console.error('❌ Failed to send status update notification:', error);
      }
    }
  };

  const deleteOrder = async (orderId) => {
    console.log('🗑️ Delete button clicked for order:', orderId);

    const confirmed = await showAlert(
      'هل أنت متأكد من حذف هذا الطلب نهائياً؟',
      'delete',
      {
        showCancel: true,
        confirmText: 'حذف نهائياً',
        cancelText: 'إلغاء'
      }
    );

    if (confirmed) {
      try {
        console.log('🗑️ Attempting to move order to bin:', orderId);
        startComponentLoading('deleteOrder', 'Moving order to bin...');

        const orderToDelete = orders.find(order => order.id === orderId);
        if (!orderToDelete) return;

        // Use new Soft Delete method (Collection based)
        const result = await firebaseService.softDeleteOrder(orderToDelete);

        if (result.success) {
          console.log(`✅ Order ${orderId} moved to bin successfully`);

          // Update local state immediately - remove from active orders
          const updatedOrders = orders.filter(order => order.id !== orderId);
          setOrders(updatedOrders);
          setFilteredOrders(updatedOrders);

          // Update localStorage
          localStorage.setItem('orders', JSON.stringify(updatedOrders));

          // We don't need to manually update deletedOrders in localStorage anymore 
          // because DeletedOrders page will fetch from collection.
          // But for immediate consistency if the user navigates there:
          const deletedOrders = JSON.parse(localStorage.getItem('deletedOrders') || '[]');
          deletedOrders.push({ ...orderToDelete, deletedAt: new Date().toISOString() });
          localStorage.setItem('deletedOrders', JSON.stringify(deletedOrders));

          await showAlert(
            'تم نقل الطلب إلى سلة المحذوفات بنجاح',
            'success'
          );
        } else {
          console.error('❌ Failed to move order to bin:', result.error);
          await showAlert(`حدث خطأ في حذف الطلب: ${result.error}`, 'error');
        }
      } catch (error) {
        console.error('❌ Error deleting order:', error);
        await showAlert(`حدث خطأ في حذف الطلب: ${error.message}`, 'error');
      } finally {
        stopComponentLoading('deleteOrder');
      }
    } else {
      console.log('❌ Delete operation cancelled by user');
    }
  };





  // WallZ Secret: Delete all orders with password protection
  const deleteAllOrders = () => {
    console.log('🗑️ WallZ Secret: Delete all orders requested');
    setShowPasswordDialog(true);
  };

  // Handle password confirmation
  const handlePasswordConfirm = async (password) => {
    setShowPasswordDialog(false);

    if (password !== 'WallZ') {
      await showAlert('❌ كلمة السر غير صحيحة!\n\nالوصول مرفوض.', 'error');
      console.log('❌ WallZ Secret: Wrong password entered');
      return;
    }

    console.log('✅ WallZ Secret: Correct password entered');

    // Show confirmation dialog
    const confirmed = await showAlert(
      `🔐 تم التحقق من كلمة السر بنجاح!\n\nهل أنت متأكد من حذف جميع الطلبات نهائياً؟\n\nسيتم نقل ${orders.length} طلب إلى سلة المحذوفات في Firebase و localStorage.\n\nيمكنك استعادتها من صفحة المحذوفات لاحقاً.`,
      'delete',
      {
        showCancel: true,
        confirmText: 'نقل للكل لسلة الحذف',
        cancelText: 'إلغاء'
      }
    );

    if (!confirmed) {
      console.log('❌ WallZ Secret: Delete cancelled by user');
      return;
    }

    try {
      startComponentLoading('deleteAll', 'Moving all orders to bin...');

      // This now moves orders to 'deleted_orders' collection in Firebase in a batch!
      const firebaseResult = await firebaseService.deleteAllOrders();

      if (firebaseResult.success && firebaseResult.deletedCount > 0) {
        console.log('✅ Delete All Orders: Firebase bulk soft-delete successful');

        // Clear active orders completely from state and local storage
        localStorage.removeItem('orders');
        setOrders([]);
        setFilteredOrders([]);
        setNewOrders(new Set());

        // Notify other devices/tabs
        window.dispatchEvent(new CustomEvent('ordersUpdated'));

        await showAlert(
          `تم بنجاح نقل ${firebaseResult.deletedCount} طلب إلى سلة المحذوفات. ستتم المزامنة على جميع الأجهزة.`,
          'success'
        );
      } else if (firebaseResult.deletedCount === 0) {
        await showAlert('لا توجد طلبات لنقلها حالياً.', 'info');
      } else {
        console.error('❌ Failed to delete all orders:', firebaseResult.error);
        await showAlert(`حدث خطأ أثناء الحذف: ${firebaseResult.error}`, 'error');
      }
    } catch (error) {
      console.error('❌ Error in deleteAllOrders flow:', error);
      await showAlert(`حدث خطأ غير متوقع: ${error.message}`, 'error');
    } finally {
      stopComponentLoading('deleteAll');
    }
  };


  // Handle maintenance mode toggle
  const handleMaintenanceToggle = async () => {
    const newVal = !maintenanceMode;
    const label = newVal ? 'ENABLE' : 'DISABLE';
    const confirmed = await showAlert(
      newVal
        ? '🔒 Are you sure you want to enable Maintenance Mode?\n\nVisitors will see the "Coming Soon" page until you turn it off.'
        : '✅ Are you sure you want to disable Maintenance Mode?\n\nThe site will go live immediately for all visitors.',
      newVal ? 'delete' : 'success',
      { showCancel: true, confirmText: `Yes, ${label}`, cancelText: 'Cancel' }
    );
    if (!confirmed) return;

    setMaintenanceLoading(true);
    const result = await firebaseService.setMaintenanceMode(newVal);
    setMaintenanceLoading(false);

    if (result.success) {
      await showAlert(
        newVal
          ? '🔒 Maintenance Mode is now ENABLED. Visitors see the Coming Soon page.'
          : '✅ Maintenance Mode is now DISABLED. The site is live!',
        newVal ? 'delete' : 'success'
      );
    } else {
      await showAlert('❌ Failed to update Maintenance Mode: ' + result.error, 'error');
    }
  };

  // Handle password dialog cancel
  const handlePasswordCancel = () => {
    setShowPasswordDialog(false);
    console.log('❌ WallZ Secret: Password dialog cancelled by user');
  };

  // Handle print order
  const handlePrintOrder = (order) => {
    setPrintOrderData(order);
    setShowPrintOrder(true);
  };

  const handleClosePrintOrder = () => {
    setShowPrintOrder(false);
    setPrintOrderData(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#f39c12';
      case 'confirmed': return '#27ae60';
      case 'shipped': return '#3498db';
      case 'delivered': return '#2ecc71';
      case 'cancelled': return '#e74c3c';
      default: return '#95a5a6';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'confirmed': return 'Confirmed';
      case 'shipped': return 'Shipped';
      case 'delivered': return 'Delivered';
      case 'cancelled': return 'Cancelled';
      default: return 'Unknown';
    }
  };

  const getTotalOrders = () => filteredOrders.length;
  const getTotalRevenue = () => {
    // Use filtered orders to exclude deleted orders
    return filteredOrders.reduce((sum, order) => {
      // استبعاد الطلبات الملغية
      if (order.status === 'cancelled') {
        return sum;
      }

      // حساب السعر بدون الشحن (subtotal فقط)
      const subtotal = order.subtotal || (order.total - (order.shipping || order.shippingCost || 0)) || 0;
      return sum + subtotal;
    }, 0);
  };


  // Check if order is new (within last 24 hours)
  const isNewOrder = (order) => {
    return newOrders.has(order.id);
  };

  // Hide notification function
  const hideNotification = () => {
    setShowNotification(false);
    setNotificationCount(0);
  };

  // Export orders to Excel
  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Orders');

      // Add headers
      worksheet.columns = [
        { header: 'Order ID', key: 'id', width: 15 },
        { header: 'Date', key: 'date', width: 20 },
        { header: 'Customer Name', key: 'customerName', width: 25 },
        { header: 'Phone 1', key: 'phone1', width: 15 },
        { header: 'Phone 2', key: 'phone2', width: 15 },
        { header: 'Governorate', key: 'governorate', width: 20 },
        { header: 'Address', key: 'address', width: 30 },
        { header: 'Status', key: 'status', width: 15 },
        { header: 'Items Count', key: 'itemsCount', width: 12 },
        { header: 'Subtotal', key: 'subtotal', width: 15 },
        { header: 'Shipping', key: 'shipping', width: 15 },
        { header: 'Total', key: 'total', width: 15 },
        { header: 'Items Details', key: 'itemsDetails', width: 50 }
      ];

      // Style headers
      worksheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFF' } };
      worksheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE1306C' }
      };

      // Add data rows - use filteredOrders to exclude deleted orders
      filteredOrders.forEach(order => {
        const customer = order.customer || order.customerData || {};
        const itemsDetails = order.items?.map(item =>
          `${item.name} (${item.size}, ${item.color}) x${item.quantity} = ${item.price * item.quantity} EGP`
        ).join('; ') || 'No items';

        worksheet.addRow({
          id: order.id,
          date: new Date(order.date).toLocaleDateString('en-GB'),
          customerName: getFullCustomerName(order),
          phone1: customer.phone1 || 'N/A',
          phone2: customer.phone2 || 'N/A',
          governorate: customer.governorate || 'N/A',
          address: customer.address || 'N/A',
          status: order.status,
          itemsCount: order.items?.length || 0,
          subtotal: order.subtotal || (order.total - (order.shipping || order.shippingCost || 0)) || 0,
          shipping: order.shipping || order.shippingCost || 0,
          total: order.total || order.finalTotal || 0,
          itemsDetails: itemsDetails
        });
      });

      // Auto-fit columns
      worksheet.columns.forEach(column => {
        column.width = Math.max(column.width, 10);
      });

      // Add summary row
      const summaryRow = worksheet.addRow({});
      summaryRow.getCell(1).value = 'SUMMARY';
      summaryRow.getCell(1).font = { bold: true };

      summaryRow.getCell(9).value = `Total Orders: ${filteredOrders.length}`;
      summaryRow.getCell(10).value = `Total Revenue: ${getTotalRevenue()} EGP`;
      summaryRow.getCell(9).font = { bold: true };
      summaryRow.getCell(10).font = { bold: true };

      // Generate and download file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `orders_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    }
  };

  return (
    <motion.div
      className="admin-orders-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Loading Overlay */}
      {isComponentLoading('loadOrders') && (
        <div className="loading-overlay">
          <Loading
            size="large"
            color="primary"
            text="Loading orders..."
            overlay={true}
          />
        </div>
      )}

      {/* Header */}
      <div className="admin-header">
        <div className="admin-header-content">
          <div className="header-top">
            <button
              onClick={() => navigate('/')}
              className="back-to-home-btn"
            >
              ← Back to Home
            </button>
            <h1>📊 Orders Management</h1>
          </div>
          <div className="admin-stats">
            <div className="stat-card">
              <FaShoppingCart />
              <div>
                <span className="stat-number">{getTotalOrders()}</span>
                <span className="stat-label">Total Orders</span>
              </div>
            </div>
            <div className="stat-card">
              <FaCheckCircle />
              <div>
                <span className="stat-number">${getTotalRevenue()}</span>
                <span className="stat-label">Total Revenue</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="admin-filters">
        <div className="search-box">
          <FaSearch />
          <input
            type="text"
            placeholder="Search by name, phone, or location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-box">
          <FaFilter />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="shipped">Shipped</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div className="sort-box">
          <FaFilter />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="newest">🕒 Newest First</option>
            <option value="oldest">🕐 Oldest First</option>
            <option value="highest">💰 Highest Price</option>
            <option value="lowest">💵 Lowest Price</option>
            <option value="name-asc">👤 Name A-Z</option>
            <option value="name-desc">👤 Name Z-A</option>
          </select>
        </div>
        <button
          onClick={deleteAllOrders}
          className="delete-all-orders-btn"
          disabled={isComponentLoading('deleteAll')}
        >
          {isComponentLoading('deleteAll') ? (
            <>
              <Loading size="small" color="light" showText={false} />
              <span style={{ marginLeft: '8px' }}>Deleting...</span>
            </>
          ) : (
            <>
              🗑️
              Delete All Orders
            </>
          )}
        </button>
        <button onClick={exportToExcel} className="export-excel-btn">
          <FaFileExcel />
          Export to Excel
        </button>
        <button
          onClick={() => setShowNotificationManager(!showNotificationManager)}
          className="notification-settings-btn"
        >
          <FaBell />
          Notification Settings
        </button>
        <Link to="/admin/content" className="deleted-orders-btn" style={{ background: 'rgba(52, 152, 219, 0.1)', color: '#3498db', borderColor: 'rgba(52, 152, 219, 0.3)' }}>
          <FaUser />
          Content Manager
        </Link>
        <Link to="/admin/promo-codes" className="deleted-orders-btn" style={{ background: 'rgba(197, 160, 89, 0.1)', color: '#c5a059', borderColor: 'rgba(197, 160, 89, 0.3)' }}>
          <FaTag />
          Promo Codes
        </Link>
        <Link to="/admin/deleted-orders" className="deleted-orders-btn" style={{
          display: showDeletedOrders || deletedOrdersCount > 0 ? 'flex' : 'none'
        }}>
          <FaTrash />
          Deleted Orders
          {deletedOrdersCount > 0 && (
            <span className="badge" style={{
              marginLeft: '8px',
              background: '#e74c3c',
              color: 'white',
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 'bold'
            }}>
              {deletedOrdersCount}
            </span>
          )}
        </Link>
        <button
          onClick={() => {
            if ('Notification' in window) {
              window.Notification.requestPermission().then(permission => {
                if (permission === 'granted') {
                  new window.Notification('🛍️ WallZ: Test Active!', {
                    body: 'Notifications are working correctly on your phone/browser.',
                    icon: '/logoPage.png'
                  });
                } else {
                  alert('Please enable notifications in your browser settings.');
                }
              });
            } else {
              alert('Notifications are not supported on this device.');
            }
          }}
          className="admin-btn"
          style={{ background: 'rgba(255,255,255,0.05)', color: '#fff', border: '1px solid rgba(255,255,255,0.1)' }}
        >
          <FaBell /> Test Alert
        </button>

        {/* Maintenance Mode Toggle */}
        <button
          id="maintenance-mode-toggle-btn"
          onClick={handleMaintenanceToggle}
          disabled={maintenanceLoading}
          className="maintenance-toggle-btn"
          style={{
            background: maintenanceMode
              ? 'rgba(231, 76, 60, 0.15)'
              : 'rgba(39, 174, 96, 0.08)',
            border: maintenanceMode
              ? '1px solid rgba(231, 76, 60, 0.45)'
              : '1px solid rgba(39, 174, 96, 0.35)',
            color: maintenanceMode ? '#e74c3c' : '#2ecc71',
            padding: '0 20px',
            height: 46,
            borderRadius: 8,
            cursor: maintenanceLoading ? 'not-allowed' : 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 13,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            transition: 'all 0.3s ease',
            position: 'relative',
            overflow: 'hidden',
            opacity: maintenanceLoading ? 0.6 : 1,
            fontFamily: 'var(--font-body)'
          }}
          title={maintenanceMode ? 'Click to disable maintenance mode' : 'Click to enable maintenance mode'}
        >
          {/* Animated glow ring for active state */}
          {maintenanceMode && (
            <span style={{
              position: 'absolute',
              inset: 0,
              borderRadius: 8,
              boxShadow: '0 0 20px rgba(231, 76, 60, 0.3)',
              animation: 'pulse 2s ease-in-out infinite',
              pointerEvents: 'none'
            }} />
          )}
          {/* Toggle pill */}
          <span style={{
            display: 'inline-flex',
            width: 36,
            height: 20,
            borderRadius: 10,
            background: maintenanceMode
              ? 'rgba(231, 76, 60, 0.6)'
              : 'rgba(39, 174, 96, 0.3)',
            border: maintenanceMode
              ? '1px solid rgba(231,76,60,0.8)'
              : '1px solid rgba(39,174,96,0.5)',
            alignItems: 'center',
            padding: '0 2px',
            transition: 'all 0.3s ease',
            flexShrink: 0
          }}>
            <span style={{
              width: 14,
              height: 14,
              borderRadius: '50%',
              background: maintenanceMode ? '#e74c3c' : '#2ecc71',
              boxShadow: maintenanceMode
                ? '0 0 8px rgba(231,76,60,0.8)'
                : '0 0 8px rgba(39,174,96,0.8)',
              transform: maintenanceMode ? 'translateX(16px)' : 'translateX(0)',
              transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
              flexShrink: 0
            }} />
          </span>
          {maintenanceLoading ? 'Updating...' : maintenanceMode ? '🔒 Maintenance: ON' : '🌐 Maintenance: OFF'}
        </button>
      </div>

      {/* Orders List */}
      <div className="orders-list">
        {!filteredOrders || filteredOrders.length === 0 ? (
          <div className="no-orders">
            <FaShoppingCart />
            <h3>No Orders Found</h3>
            <p>No orders match the specified criteria</p>
          </div>
        ) : (
          filteredOrders.map((order) => (
            <div key={order.id} className="order-card">
              <div className="order-header">
                <div className="order-info">
                  <div className="order-title-row">
                    <h3>Order #{order.id.slice(-6)}</h3>
                    {isNewOrder(order) && (
                      <span className="new-badge">New</span>
                    )}
                  </div>
                  <span className="order-date">
                    <FaCalendarAlt />
                    {formatDate(order.orderDate)}
                  </span>
                </div>
                <div className="order-status">
                  <span
                    className="status-badge"
                    style={{ backgroundColor: getStatusColor(order.status) }}
                  >
                    {getStatusText(order.status)}
                  </span>
                </div>
              </div>

              <div className="order-customer">
                <div className="customer-info">
                  <FaUser />
                  <span>{getFullCustomerName(order)}</span>
                </div>
                <div className="customer-location">
                  <FaMapMarkerAlt />
                  <span>{order.customer?.governorate || order.customerData?.governorate || 'N/A'}</span>
                </div>
                <div className="customer-phone">
                  <FaPhone />
                  <span>{order.customer?.phone1 || order.customerData?.phone1 || 'N/A'}</span>
                </div>
              </div>

              <div className="order-summary">
                <div className="order-items">
                  {order.items.some(it => it.isCustom && it.customImage) && (
                    <div style={{ display: 'flex', gap: '5px', marginBottom: '8px' }}>
                      {order.items
                        .filter(it => it.isCustom && it.customImage)
                        .slice(0, 3)
                        .map((it, idx) => (
                          <img
                            key={idx}
                            src={it.customImage}
                            alt="Preview"
                            style={{
                              width: '40px',
                              height: '52px',
                              objectFit: 'cover',
                              borderRadius: '4px',
                              border: '1px solid rgba(197, 160, 89, 0.4)'
                            }}
                          />
                        ))}
                    </div>
                  )}
                  <span>{order.items.length} items</span>
                </div>
                <div className="order-total">
                  <span>{order.total || order.finalTotal || (order.subtotal + (order.shipping || order.shippingCost || 0)) || 0} EGP</span>

                </div>
              </div>

              <div className="order-actions">
                <button
                  onClick={() => {
                    setSelectedOrder(order);
                    setShowOrderDetails(true);
                  }}
                  className="view-btn"
                >
                  <FaEye />
                  View Details
                </button>

                <button
                  onClick={() => handlePrintOrder(order)}
                  className="print-btn"
                >
                  🖨️ Print
                </button>

                <div className="status-actions">
                  <select
                    value={order.status}
                    onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                    className="status-select"
                  >
                    <option value="pending">⏳ Pending</option>
                    <option value="confirmed">✅ Confirmed</option>
                    <option value="shipped">🚚 Shipped</option>
                    <option value="delivered">📦 Delivered</option>
                    <option value="cancelled">❌ Cancelled</option>
                  </select>
                </div>

                <button
                  onClick={() => deleteOrder(order.id)}
                  className="delete-btn"
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Order Details Modal */}
      {
        showOrderDetails && selectedOrder && (
          <div className="modal-overlay" onClick={() => setShowOrderDetails(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Order Details #{selectedOrder.id.slice(-6)}</h2>
                <button
                  onClick={() => setShowOrderDetails(false)}
                  className="close-btn"
                >
                  ×
                </button>
              </div>

              <div className="modal-body">
                <div className="customer-details">
                  <h3>👤 Customer Information</h3>
                  <div className="detail-row">
                    <strong>Full Name:</strong>
                    <span>{getFullCustomerName(selectedOrder)}</span>
                  </div>
                  <div className="detail-row">
                    <strong>Governorate:</strong>
                    <span>{selectedOrder.customer?.governorate || selectedOrder.customerData?.governorate || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <strong>Address:</strong>
                    <span>{selectedOrder.customer?.address || selectedOrder.customerData?.address || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <strong>Phone 1:</strong>
                    <span>{selectedOrder.customer?.phone1 || selectedOrder.customerData?.phone1 || 'N/A'}</span>
                  </div>
                  {(selectedOrder.customer?.phone2 || selectedOrder.customerData?.phone2) && (
                    <div className="detail-row">
                      <strong>Phone 2:</strong>
                      <span>{selectedOrder.customer?.phone2 || selectedOrder.customerData?.phone2}</span>
                    </div>
                  )}
                </div>

                <div className="order-items-details">
                  <h3>🛍️ Ordered Items</h3>
                  {selectedOrder.items.map((item, index) => (
                    <div key={index} className="item-detail">
                      {item.isCustom && item.customImage ? (
                        /* ── Custom poster: large image preview + download ── */
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                            <a
                              href={item.customImage}
                              download={`custom-poster-${selectedOrder.id?.slice(-6) ?? index}.jpg`}
                              title="Download customer image"
                            >
                              <img
                                src={item.customImage}
                                alt="Customer's custom design"
                                style={{ width: '120px', height: '160px', objectFit: 'cover', borderRadius: '4px', border: '1px solid rgba(197,160,89,0.4)' }}
                              />
                            </a>
                            <div className="item-info">
                              <span style={{ display: 'inline-block', background: 'rgba(197,160,89,0.15)', color: '#c5a059', padding: '2px 10px', borderRadius: '12px', fontSize: '0.75rem', marginBottom: '8px', border: '1px solid rgba(197,160,89,0.3)' }}>
                                ✦ Custom Poster
                              </span>
                              <h4>{item.name}</h4>
                              <p>Size: {item.size} {item.dimensions ? `(${item.dimensions})` : ''}</p>
                              <p>Frame: {item.color}</p>
                              {item.orientation && (
                                <p>Orientation: <strong style={{ color: '#c5a059' }}>{item.orientation === 'vertical' ? 'عمودي (Vertical)' : 'أفقي (Horizontal)'}</strong></p>
                              )}
                              <p>Quantity: {item.quantity}</p>
                              <p>Price: {item.price * item.quantity} EGP</p>
                              <a
                                href={item.customImage}
                                download={`custom-poster-${selectedOrder.id?.slice(-6) ?? index}.jpg`}
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                                  marginTop: '10px', padding: '7px 16px',
                                  background: 'rgba(197,160,89,0.15)', color: '#c5a059',
                                  border: '1px solid rgba(197,160,89,0.4)', borderRadius: '4px',
                                  fontSize: '0.8rem', fontWeight: '600', textDecoration: 'none',
                                  cursor: 'pointer', letterSpacing: '0.5px',
                                  transition: 'background 0.2s'
                                }}
                              >
                                ⬇️ Download Customer Image
                              </a>
                            </div>
                          </div>
                        </div>
                      ) : (
                        /* ── Regular product ── */
                        <>
                          <Link
                            to={`/product/${item.productId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="item-image-link"
                          >
                            <img src={item.image} alt={item.name} />
                          </Link>
                          <div className="item-info">
                            <Link
                              to={`/product/${item.productId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="item-name-link"
                            >
                              <h4>{item.name}</h4>
                            </Link>
                            <p>{item.category} - {item.size}</p>
                            <p>Color: {item.color}</p>
                            <p>Quantity: {item.quantity}</p>
                            <p>Price: {item.price * item.quantity} EGP</p>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>

                <div className="order-total-details">
                  <h3>💰 Order Summary</h3>
                  <div className="total-row">
                    <span>Subtotal:</span>
                    {selectedOrder.appliedPromo ? (
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>{selectedOrder.subtotal || 0} EGP</span>
                        <span style={{ color: '#4CAF50' }}>{(selectedOrder.subtotal || 0) - (selectedOrder.discount || 0)} EGP</span>
                      </div>
                    ) : (
                      <span>{selectedOrder.subtotal || selectedOrder.totalPrice || 0} EGP</span>
                    )}
                  </div>
                  {selectedOrder.appliedPromo && (
                    <div className="total-row" style={{ color: '#4CAF50', borderTop: '1px dashed rgba(76, 175, 80, 0.2)', paddingTop: '8px', marginTop: '5px' }}>
                      <span>Promo Code ({selectedOrder.appliedPromo.code}):</span>
                      <span>-{selectedOrder.discount || 0} EGP</span>
                    </div>
                  )}
                  <div className="total-row">
                    <span>Shipping:</span>
                    <span>{selectedOrder.shipping || selectedOrder.shippingCost || 0} EGP</span>
                  </div>
                  <div className="total-row final">
                    <span>Total:</span>
                    <span>{selectedOrder.total || selectedOrder.finalTotal || 0} EGP</span>
                  </div>
                </div>

              </div >

              {/* Print Button */}
              < div className="modal-actions" >
                <button
                  onClick={() => handlePrintOrder(selectedOrder)}
                  className="print-order-btn"
                >
                  🖨️ Print Order
                </button>
                <button
                  onClick={() => setShowOrderDetails(false)}
                  className="close-modal-btn"
                >
                  ✕ Close
                </button>
              </div >
            </div >
          </div >
        )
      }

      {/* Custom Alert */}
      <CustomAlert
        show={alertState.show}
        message={alertState.message}
        type={alertState.type}
        onConfirm={alertState.onConfirm}
        onCancel={alertState.onCancel}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
      />

      <Notification
        show={showNotification}
        count={notificationCount}
        onClose={hideNotification}
      />

      {/* Password Dialog */}
      <PasswordDialog
        show={showPasswordDialog}
        onConfirm={handlePasswordConfirm}
        onCancel={handlePasswordCancel}
        title="Delete All Orders"
        message="أدخل كلمة السر للوصول لوظيفة حذف جميع الطلبات نهائياً"
        correctPassword="WallZ"
      />

      {/* Notification Manager */}
      {
        showNotificationManager && (
          <div className="notification-manager-overlay">
            <div className="notification-manager-modal">
              <div className="modal-header">
                <h3>🔔 Notification Settings</h3>
                <button
                  className="close-btn"
                  onClick={() => setShowNotificationManager(false)}
                >
                  ✕
                </button>
              </div>
              <NotificationManager />
            </div>
          </div>
        )
      }

      {/* Print Order Modal */}
      {
        showPrintOrder && printOrderData && (
          <PrintOrder
            order={printOrderData}
            onClose={handleClosePrintOrder}
          />
        )
      }
    </motion.div >
  );
}

export default AdminOrders;
